/**
 * Phase 128 P1 — Visible payment-mode banner.
 *
 * Surfaces the current payment environment ("TEST MODE", "LIVE", or
 * "MOCK MODE — payments not configured") so operators and players are
 * never confused about whether a transaction is real or simulated.
 *
 * Reads from the public `/arenax/payment-config` endpoint — NO secrets
 * leave the backend.  The component is intentionally compact, ambient,
 * and never blocks the booking flow.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, FlaskConical, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

export default function ArenaxPaymentModeBanner({
  variant = 'pill',     // 'pill' | 'strip' | 'inline'
  hideWhenLive = false, // suppress when configured for live (operators may want quiet)
  testId = 'arenax-payment-mode-banner',
}) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/arenax/payment-config')
      .then(r => { if (mounted) setCfg(r.data || {}); })
      .catch(() => { if (mounted) setCfg({}); });
    return () => { mounted = false; };
  }, []);

  if (!cfg) return null;

  const isMock = !cfg.configured;
  const isTest = cfg.mode === 'test' && cfg.configured;
  const isLive = cfg.mode === 'live' && cfg.configured;

  if (isLive && hideWhenLive) return null;

  let palette;
  let Icon;
  let label;
  let sublabel;

  if (isMock) {
    palette = { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.35)', accent: '#b45309' };
    Icon = AlertTriangle;
    label = 'MOCK MODE';
    sublabel = 'No payment keys configured — slots auto-confirm without real money';
  } else if (isTest) {
    palette = { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.30)', accent: '#0e7490' };
    Icon = FlaskConical;
    label = 'TEST MODE';
    sublabel = 'Sandbox payments — use Razorpay test cards. No real money moves.';
  } else {
    palette = { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.30)', accent: '#047857' };
    Icon = ShieldCheck;
    label = 'LIVE';
    sublabel = 'Real payments active.';
  }

  if (variant === 'pill') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase"
        style={{ background: palette.bg, color: palette.accent, border: `1px solid ${palette.border}` }}
        data-testid={`${testId}-pill`}
        title={sublabel}
      >
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  }

  if (variant === 'inline') {
    return (
      <div
        className="flex items-center gap-2 text-[11px] font-medium"
        style={{ color: palette.accent }}
        data-testid={`${testId}-inline`}
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="uppercase tracking-wider font-bold">{label}</span>
        <span className="opacity-75">·</span>
        <span className="opacity-90">{sublabel}</span>
      </div>
    );
  }

  // strip — full-width attention banner
  return (
    <div
      className="rounded-lg px-4 py-2.5 flex items-center gap-3"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
      data-testid={`${testId}-strip`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" style={{ color: palette.accent }} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-black"
             style={{ color: palette.accent }}>
          {label}{isTest && ' · Sandbox payments enabled'}
        </div>
        <div className="text-[11px] mt-0.5 leading-snug"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
          {sublabel}
        </div>
      </div>
    </div>
  );
}
